import { useRef, useState } from 'react';
import { useEscapeKey } from './Files';
import styles from './videoPlayerModal.module.css';

interface VideoPlayerModalProps {
  videoUrl: string;
  onClose: () => void;
}

export const VideoPlayerModal = ({ videoUrl, onClose }: VideoPlayerModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Handle video metadata load
  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  // Handle video errors
  const handleVideoError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  // Handle video can play (ready to start)
  const handleCanPlay = () => {
    setIsLoading(false);
  };

  useEscapeKey(onClose);

  if (!videoUrl) return;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={onClose}
      style={{
        zIndex: 9999,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        className={styles.modalContent}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
            return;
          }
          e.stopPropagation();
        }}
      >
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#666',
            }}
          >
            Loading video...
          </div>
        )}
        {hasError && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#ff4d4f',
            }}
          >
            Error loading video
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose();
            }
          }}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: '#666',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            fontSize: '16px',
            zIndex: 10000,
          }}
        >
          ×
        </button>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          webkit-playsinline="true"
          preload="metadata"
          muted
          onLoadedMetadata={handleVideoLoad}
          onError={handleVideoError}
          onCanPlay={handleCanPlay}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose();
              return;
            }
            e.stopPropagation();
          }}
          className={styles.videoElement}
          style={{
            display: isLoading || hasError ? 'none' : 'block',
            cursor: 'pointer',
            maxHeight: '90vh',
            maxWidth: '90vw',
          }}
        />
      </div>
    </div>
  );
};
